import {useCallback, useMemo} from 'react'
import {SEARCH_ROUTE} from 'constants/index'
import {SearchUrlParams} from 'modules/search/types'
import url from 'utils/url'

import {useRouter} from '@daedalus/core/src/_web/router/context/RouterContext'
import {parseQueryString} from '@daedalus/core/src/utils/url'

import {ensureBooleanUrlParams} from './ensureBooleanUrlParams'

type UpdateMethod = 'navigate' | 'push' | 'replace'

export type UpdateLocationOptionsType = {
  mergeWithCurrentParams?: boolean
  method?: UpdateMethod
  state?: Record<string, unknown>
  basePath?: string
}

export type UpdateLocationCallbackType = (
  params: SearchUrlParams,
  options?: UpdateLocationOptionsType
) => void

interface UrlUpdateFunctions {
  changeLocation: UpdateLocationCallbackType
  navigateWithParameters: UpdateLocationCallbackType
  updateLocation: UpdateLocationCallbackType
}

export const useUrlUpdater = (): UrlUpdateFunctions => {
  const {replace, push} = useRouter()

  /**
   * Update current location while preserving existing search params
   */
  const updateLocation = useCallback(
    (
      params: SearchUrlParams,
      {
        basePath = SEARCH_ROUTE,
        mergeWithCurrentParams = true,
        method = 'push'
      }: UpdateLocationOptionsType = {}
    ): void => {
      let path: string
      if (mergeWithCurrentParams) {
        // Merge new params with current search params
        const searchParams =
          typeof window !== 'undefined'
            ? parseQueryString(window.location.search)
            : {}

        path = url.stringify({
          ...searchParams,
          ...ensureBooleanUrlParams(params)
        })
      } else {
        // Fully change location without preserving current search params
        path = url.stringify(ensureBooleanUrlParams(params))
      }

      const fullPath = path ? `${basePath}?${path}` : basePath
      switch (method) {
        case 'navigate':
          window.location.href = fullPath
          break
        case 'push':
          push(fullPath)
          break
        case 'replace':
          replace(fullPath)
          break
      }
    },
    [replace, push]
  )

  /**
   * While preserving existing search params, navigate to new page
   */
  const navigateWithParameters = useCallback(
    (
      params: SearchUrlParams,
      options: UpdateLocationOptionsType = {}
    ): void => {
      updateLocation(params, {
        ...options,
        method: 'navigate'
      })
    },
    [updateLocation]
  )

  /**
   * Fully change location
   */
  const changeLocation = useCallback(
    (
      params: SearchUrlParams,
      options: UpdateLocationOptionsType = {}
    ): void => {
      updateLocation(params, {
        ...options,
        mergeWithCurrentParams: false
      })
    },
    [updateLocation]
  )

  return useMemo(
    () => ({
      updateLocation,
      navigateWithParameters,
      changeLocation
    }),
    [updateLocation, navigateWithParameters, changeLocation]
  )
}
